import React, { useState, useEffect } from 'react';
import { Button, message, Row, Col, Modal, Input, Alert, Tooltip } from 'antd';
import Confetti from 'react-confetti';
import { StoreContainer, ProfileCard, ProfileMeta, SocialButtons, AddHeadline, CustomAvatar, ScrollableContainer } from './styled';
import Header from "../../components/Header";
import BottomNavBar from "../../components/BottomNavBar";
import MyStoreTabs from '../../components/MyStoreTabs';
import { DndProvider } from 'react-dnd';
import { Link } from 'react-router-dom';
import { ROUTE_PATH } from '../../constants/routePath';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BaseButton } from '../../components/Button';
import { useStore } from "../../hooks/storeContext";
import { Spin, Button as AntButton } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MovableItem from './moveableItem';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faFacebookF, faTwitter, faYoutube, faLinkedinIn, faTiktok, faSnapchat, faReddit, faWhatsapp, faTumblr, faSoundcloud, faGithub, faSpotify, faMedium, faTwitch, faPinterest, faDiscord, faApple, faBluesky } from '@fortawesome/free-brands-svg-icons';
import { faAt, faPencil, faPlus, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../constants/colors';
import { Wand2 } from 'lucide-react';
import { WelcomeModal } from '../Dashboard/WelcomeModal';


const socialMediaLinks = [
    { name: 'instagram', icon: <FontAwesomeIcon icon={faInstagram} /> },
    { name: 'facebook', icon: <FontAwesomeIcon icon={faFacebookF} /> },
    { name: 'twitter', icon: <FontAwesomeIcon icon={faTwitter} /> },
    { name: 'youtube', icon: <FontAwesomeIcon icon={faYoutube} /> },
    { name: 'linkedin', icon: <FontAwesomeIcon icon={faLinkedinIn} /> },
    { name: 'tiktok', icon: <FontAwesomeIcon icon={faTiktok} /> },
    { name: 'threads', icon: <FontAwesomeIcon icon={faAt} /> },
    { name: 'snapchat', icon: <FontAwesomeIcon icon={faSnapchat} /> },
    { name: 'bluesky', icon: <FontAwesomeIcon icon={faBluesky} /> },
    { name: 'reddit', icon: <FontAwesomeIcon icon={faReddit} /> },
    { name: 'whatsapp', icon: <FontAwesomeIcon icon={faWhatsapp} /> },
    { name: 'tumblr', icon: <FontAwesomeIcon icon={faTumblr} /> },
    { name: 'soundcloud', icon: <FontAwesomeIcon icon={faSoundcloud} /> },
    { name: 'github', icon: <FontAwesomeIcon icon={faGithub} /> },
    { name: 'spotify', icon: <FontAwesomeIcon icon={faSpotify} /> },
    { name: 'apple', icon: <FontAwesomeIcon icon={faApple} /> },
    { name: 'medium', icon: <FontAwesomeIcon icon={faMedium} /> },
    { name: 'twitch', icon: <FontAwesomeIcon icon={faTwitch} /> },
    { name: 'pinterest', icon: <FontAwesomeIcon icon={faPinterest} /> },
    { name: 'discord', icon: <FontAwesomeIcon icon={faDiscord} /> },
    { name: 'email', icon: <FontAwesomeIcon icon={faEnvelope} /> },
];

const MyStore = () => {
    const [activeTab, setActiveTab] = useState('store');
    const { authStore } = useStore();
    const [blocks, setBlocks] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentHeadline, setCurrentHeadline] = useState('');
    const [currentId, setCurrentId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false); // Controls WelcomeModal visibility
    const [showConfetti, setShowConfetti] = useState(false); // Controls Confetti visibility
    const [confettiDimensions, setConfettiDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setConfettiDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkFirstVisit = async () => {
            try {
                const isFirstVisit = await authStore.fetchIsFirstVisit(); // Replace with your API call for first-visit check
                if (isFirstVisit) {
                    setShowModal(true);
                    setShowConfetti(true);
                }
            } catch (error) {
                console.error("Error fetching visit status:", error);
            }
        };

        checkFirstVisit();
    }, []);

    const handleCloseModal = async () => {
        setShowModal(false);
        setShowConfetti(false);
        try {
            await authStore.updateDashboardVisitFlag(); // Replace with your API call to update visit status
        } catch (error) {
            console.error("Error updating visit flag:", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await authStore.fetchUserDetailsMyStore();
                await authStore.fetchProducts();
                await authStore.fetchHeadlines();

                const combinedBlocks = [
                    ...authStore.products.map(product => ({
                        productType: product.type,
                        id: product._id,
                        text: product.title,
                        order: product.order,
                        cardType: product.cardType,
                        type: 'product',
                        thumbnail: product.thumbnail,
                        price: product.price !== undefined ? product.price : null,
                        isPublished: product.isPublished,
                        creatorImages: product.creatorImages || [],
                        creatorImagesV2: product.creatorImagesV2 || [],
                    })),
                    ...authStore.headlineSections.map(headline => ({
                        id: headline._id,
                        text: headline.headline,
                        order: headline.order,
                        type: 'headline'
                    })),
                ];

                combinedBlocks.sort((a, b) => a.order - b.order);

                setBlocks(combinedBlocks.map(block => ({
                    ...block,
                    isPublished: block.type === 'product' ? block.isPublished : true,
                })));

            } catch (error) {
                message.error('Failed to fetch products or headlines');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [authStore]);

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    const addHeadline = async () => {
        try {
            const newHeadline = await authStore.createHeadlineSection();
            setBlocks([...blocks, { id: newHeadline._id, text: newHeadline.headline, order: newHeadline.order, type: 'headline' }]);
        } catch (error) {
            message.error('Failed to create headline');
        }
    };

    const deleteBlock = async (id, type, productType) => {
        try {
            if (type === 'product') {
                if (productType === 'booking') {
                    await authStore.deleteBookingProduct(id);
                } else {
                    await authStore.deleteProduct(id);
                }
            } else if (type === 'headline') {
                await authStore.deleteHeadlineSection(id);
            }
            setBlocks(blocks.filter(block => block.id !== id));
        } catch (error) {
            message.error('Failed to delete block');
        }
    };

    const editBlock = (id, type, text, productType) => {
        try {
            if (type === 'product') {
                switch (productType.toLowerCase()) {
                    case 'url':
                        navigate(`/products/edit/url/${id}`);
                        break;
                    case 'digital':
                        navigate(`/products/edit/digital/${id}`);
                        break;
                    case 'custom':
                        navigate(`/products/edit/custom/${id}`);
                        break;
                    case 'email':
                        navigate(`/products/edit/email/${id}`);
                        break;
                    case 'video':
                        navigate(`/products/edit/video/${id}`);
                        break;
                    case 'referral':
                        navigate(`/products/edit/referral/${id}`);
                        break;
                    case 'booking':
                        navigate(`/products/edit/sparkle-connect/${id}`);
                        break;
                    case 'spotify':
                        navigate(`/products/edit/spotify/${id}`);
                        break;
                    case 'tip':
                        navigate(`/products/edit/tip-jar/${id}`);
                        break;
                    default:
                        message.error('Unknown product type');
                }
            } else if (type === 'headline') {
                setCurrentId(id);
                setCurrentHeadline(text);
                setIsModalVisible(true);
            }
        } catch (error) {
            message.error('Failed to edit block');
        }
    };

    const handleOk = async () => {
        try {
            await authStore.updateHeadlineSection(currentId, { headline: currentHeadline });
            setBlocks(blocks.map(block => block.id === currentId ? { ...block, text: currentHeadline } : block));
            setIsModalVisible(false);
        } catch (error) {
            message.error('Failed to update headline');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const togglePublish = async (id) => {
        try {
            const block = blocks.find(b => b.id === id);
            if (!block) {
                throw new Error('Block not found');
            }

            if (block.isPublished) {
                await authStore.unpublishProduct(id);
            } else {
                // Assuming there's a publishProduct method in authStore
                await authStore.publishProduct(id);
            }

            // Update the local state to reflect the change
            setBlocks(blocks.map(b =>
                b.id === id ? { ...b, isPublished: !b.isPublished } : b
            ));

            message.success(`Product ${block.isPublished ? 'unpublished' : 'published'}`);
        } catch (error) {
            message.error(`Failed`);
        }
    };

    const moveBlock = (fromIndex, toIndex) => {
        setBlocks((prevBlocks) => {
            const updatedBlocks = [...prevBlocks];
            const [removed] = updatedBlocks.splice(fromIndex, 1);
            updatedBlocks.splice(toIndex, 0, removed);
            return updatedBlocks;
        });

        setIsDragging(true);
    };

    useEffect(() => {
        if (isDragging) {
            const timer = setTimeout(() => {
                updateBlockOrderOnDragEnd();
                setIsDragging(false);
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [isDragging, blocks]);

    const updateBlockOrderOnDragEnd = async () => {
        try {
            await authStore.updateBlockOrder(blocks.map((block, index) => ({ ...block, order: index })));
        } catch (error) {
            message.error('Failed to update order');
        }
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Header />
            <MyStoreTabs activeTab={activeTab} onTabChange={handleTabChange} />
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '77vh' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                </div>
            ) : (
                <StoreContainer>
                    <ProfileCard>
                        <Row>
                            <Col xs={8} sm={6} md={6} lg={6} xl={6}>
                                <CustomAvatar
                                    size={64}
                                    src={authStore.me?.avatar || null}
                                    style={{
                                        background: authStore.me?.avatar ? 'none' : '#000000',
                                        fontSize: '32px',
                                        fontWeight: '600',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        textAlign: 'center',
                                    }}
                                >
                                    {authStore.me?.avatar ? null : authStore.me?.userName?.charAt(0).toUpperCase()}
                                </CustomAvatar>
                            </Col>
                            <Col xs={16} sm={18} md={18} lg={18} xl={18}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <ProfileMeta style={{ marginBottom: '15px' }}
                                        title={authStore.me.userName}
                                        description={authStore.me.bio}
                                    />
                                    <Link to={ROUTE_PATH.USERPROFILE}>
                                        <Button style={{ border: '1px solid lightgrey' }} type="text" icon={<FontAwesomeIcon icon={faPencil} />} />
                                    </Link>
                                </div>
                                <ScrollableContainer>
                                    <SocialButtons>
                                        {socialMediaLinks.map(({ name, icon }) => {
                                            const url = authStore.me.socialLinks?.[name];
                                            const username = url && url.startsWith('http') ? url.split('/').pop() : url;
                                            const absoluteUrl = url && !url.startsWith('http') ? `https://${url}` : url;

                                            return username ? (
                                                <a href={absoluteUrl} target="_blank" rel="noopener noreferrer" key={name}>
                                                    <Button shape="circle" icon={icon} />
                                                </a>
                                            ) : null;
                                        })}
                                    </SocialButtons>
                                </ScrollableContainer>

                                <div style={{ justifySelf: 'right' }}>
                                    <Link to={ROUTE_PATH.DESIGN}>
                                        <Button
                                            type="primary"
                                            icon={<Wand2 size={16} />}
                                            style={{
                                                width: 'auto',
                                                alignItems: 'right',
                                                background: COLORS.$black,
                                                height: '40px',
                                                borderRadius: '8px',
                                                fontSize: '15px'
                                            }}
                                        >
                                            Design Page
                                        </Button>
                                    </Link>
                                </div>
                            </Col>
                        </Row>
                    </ProfileCard>

                    {(!blocks || blocks.length === 0) && (
                        <Alert
                            message="No Links Yet"
                            description="Add your first link or product to start your page. Once you have at least one product, this message will disappear."
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined />}
                            style={{ marginTop: 10, marginBottom: 20, borderRadius: '17px' }}
                            action={
                                <Tooltip title="Add Links, Digital Products, Custom Products, and more">
                                    <Link to={ROUTE_PATH.PRODUCTS}>
                                        <AntButton type="link" size="small">
                                            Add a Link
                                        </AntButton>
                                    </Link>
                                </Tooltip>
                            }
                        />
                    )}

                    {blocks.map((block, index) => (
                        <MovableItem
                            key={block.id}
                            id={block.id}
                            text={block.text}
                            type={block.type}
                            index={index}
                            moveBlock={moveBlock}
                            onDelete={deleteBlock}
                            onEdit={() => editBlock(block.id, block.type, block.text, block.productType)}
                            onTogglePublish={togglePublish}
                            thumbnail={block.thumbnail}
                            productType={block.productType}
                            price={block.price}
                            isPublished={block.isPublished}
                            cardType={block.cardType}
                            creatorImages={block.creatorImages}
                            creatorImagesV2={block.creatorImagesV2}
                            animationDelay={index * 0.1}
                        />
                    ))}
                    <Link to={ROUTE_PATH.PRODUCTS}>
                        <BaseButton>
                            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
                            Add Links
                        </BaseButton>
                    </Link>
                    <AddHeadline>
                        <span onClick={addHeadline}>Add Headline</span>
                    </AddHeadline>
                </StoreContainer>
            )}
            <BottomNavBar />
            <Modal
                title="Edit Headline"
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                <Input
                    value={currentHeadline}
                    onChange={(e) => setCurrentHeadline(e.target.value)}
                    placeholder="Section Title"
                />
            </Modal>
            {showConfetti && (
                <Confetti
                    width={confettiDimensions.width}
                    height={confettiDimensions.height}
                    numberOfPieces={150}
                    gravity={0.1}
                    wind={0.01}
                    recycle={false}
                    tweenDuration={4000}
                />
            )}

            <WelcomeModal
                showModal={showModal}
                handleClose={handleCloseModal}
            />
        </DndProvider>
    );
};

export default MyStore;
